import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {
  getBalance,
  getHistory,
  getSubscriptionId,
  getUsage,
  listImports,
  dismissImports,
} from '../../../app/modules/widgets/core/_requests'
import type {
  DismissImportsRequest,
  DismissImportsResponse,
  ImportRecord,
} from '../../../app/modules/widgets/core/importModels'
import {
  fileQueryKeys,
  folderListQuery,
  StorageRoot,
} from '../../../app/modules/widgets/core/storage'
import {dbItem, Job} from '../crud-helper/models'
import {getLogger} from '../logUtils'
import {useCognitoAuth} from '../../../app/modules/auth'
import {handleAuthError} from '../../../app/modules/auth/core/AuthErrorInterceptor'

/** Folder listing keys live with the storage layer; this name is kept for existing importers. */
export {filesListQueryKey} from '../../../app/modules/widgets/core/storage'

/** Total cloud storage usage (`GET storage/usage`). Invalidate on `files.changed` / job completion. */
export const storageUsageQueryKey = ['storageUsage'] as const

/** The user's recent server-side imports (`GET storage/import`). Invalidate on `import.status`. */
export const importsQueryKey = ['imports'] as const

/**
 * While an import is still queued or running, poll this often in case its `import.status`
 * notification never arrives (socket down or reconnecting). Idle otherwise.
 */
export const ACTIVE_IMPORTS_POLL_MS = 15_000

/** Cloud unit balance (`GET balance`). Invalidate on `job.status`. Prefix-match with `['balance']`. */
export const balanceQueryKey = (subscriptionId: string): ['balance', string] => [
  'balance',
  subscriptionId,
]

/**
 * How long inactive folder queries stay in memory. Default RQ v4 is 5 minutes, so navigating into a
 * subfolder for a few minutes dropped the parent/root cache and "go up" showed a skeleton with no
 * getQueryData hit. Keep listings much longer so navigation can reuse them (stale-while-revalidate).
 */
export const FILES_LIST_GC_TIME_MS = 1000 * 60 * 60 * 24

/** One folder's listing: `folder` is relative to `root` (`''` for the root), read as `identityId`. */
export const useFetchFiles = (
  root: StorageRoot,
  identityId: string,
  folder: string,
  opts?: {enabled?: boolean}
) => {
  const {currentUser} = useCognitoAuth()
  const queryClient = useQueryClient()
  const {queryKey, queryFn} = folderListQuery(root, identityId, folder)

  return useQuery<dbItem[] | undefined, Error>(queryKey, queryFn, {
    staleTime: currentUser ? 1000 * 60 * 2 : 0, // 2 min cache when authenticated; none otherwise
    cacheTime: FILES_LIST_GC_TIME_MS,
    keepPreviousData: true,
    // The Files panel is mounted from app start now, so this query can be reached before
    // Cognito has resolved an identity — callers pass `enabled: false` until then, to avoid
    // firing a request keyed on a path with an empty identity id.
    enabled: opts?.enabled ?? true,
    retry: false,
    onError: (error) => {
      handleAuthError(error)
      if (root.kind === 'share') {
        // A shared folder that fails (gone, or no longer readable) is that share's problem only:
        // the browser drops its listings once it has moved away (see FileTable). The user's own
        // cache stays.
        return
      }
      // Clear the query cache on auth error to force fresh data
      queryClient.removeQueries(fileQueryKeys.all)
    },
  })
}

export const useFetchHistory = ({
  startCreationTime,
  endCreationTime,
}: {
  startCreationTime: number
  endCreationTime: number
}) => {
  const {currentUser} = useCognitoAuth()
  const queryClient = useQueryClient()

  return useQuery<Job[] | undefined, Error>(
    ['history', {startCreationTime, endCreationTime}],
    async ({queryKey}) => {
      // @ts-ignore: unknown type
      const [, {startCreationTime, endCreationTime}] = queryKey
      let userId: string | undefined

      try {
        userId = await getSubscriptionId()
      } catch (error: any) {
        getLogger().error(error.message)
        // Re-throw to trigger the onError handler
        throw error
      }

      let yearBeforeNowDate = new Date(Date.now())
      if (!startCreationTime) {
        yearBeforeNowDate.setFullYear(yearBeforeNowDate.getFullYear() - 1)
      }
      return getHistory({
        userId: userId || '',
        startCreationTime: startCreationTime || yearBeforeNowDate.getTime(),
        endCreationTime: endCreationTime || Date.now(),
      })
    },
    {
      staleTime: currentUser ? 1000 * 60 * 60 : 0, // No caching when not authenticated
      enabled: true, // Always run query to trigger auth interceptor
      retry: false, // Don't retry on auth errors
      onError: (error) => {
        // Clear the query cache on auth error to force fresh data
        queryClient.removeQueries(['history'])
        handleAuthError(error)
      },
      // refetchInterval: 1000 * 60
    }
  )
}

/** Total bytes used in the user's cloud storage. Shared by dashboard, files header, jobs page. */
export const useStorageUsage = (opts?: {enabled?: boolean}) => {
  const {currentUser} = useCognitoAuth()

  return useQuery<number | undefined, Error>(storageUsageQueryKey, () => getUsage(), {
    staleTime: currentUser ? 1000 * 60 * 5 : 0,
    enabled: opts?.enabled ?? true,
    retry: false,
    // Kept in the cache, so callers can show the error (the Storage box does). A failed query has
    // no data and is stale, so it is fetched again on the next mount anyway.
    onError: handleAuthError,
  })
}

/** Available cloud units for the active subscription. Shared by dashboard and jobs page. */
export const useBalance = (subscriptionId: string | undefined | null) => {
  const {currentUser} = useCognitoAuth()
  const queryClient = useQueryClient()
  const id = subscriptionId || ''

  return useQuery<number | undefined, Error>(balanceQueryKey(id), () => getBalance(id), {
    staleTime: currentUser ? 1000 * 60 * 5 : 0,
    enabled: Boolean(id),
    retry: false,
    onError: (error) => {
      queryClient.removeQueries(['balance'])
      handleAuthError(error)
    },
  })
}

/** The user's recent imports, newest first. Polls only while one is still active. */
export const useImports = (opts?: {enabled?: boolean}) => {
  return useQuery<ImportRecord[], Error>(importsQueryKey, () => listImports(), {
    enabled: opts?.enabled ?? true,
    staleTime: 1000 * 30,
    // Fail fast like useStorageUsage: the list is a convenience, and the default 3 retries
    // would keep the dropdown on "Loading…" for seconds.
    retry: false,
    refetchInterval: (data) =>
      data?.some((i) => i.status === 'queued' || i.status === 'running') ? ACTIVE_IMPORTS_POLL_MS : false,
    onError: handleAuthError,
  })
}

const isFinishedImport = (record: ImportRecord) => record.status !== 'queued' && record.status !== 'running'

/**
 * Hides finished imports from the Imports list. Removes them from the cached list right away and
 * puts them back if the request fails.
 */
export const useDismissImports = () => {
  const queryClient = useQueryClient()
  return useMutation<DismissImportsResponse, Error, DismissImportsRequest, {previous?: ImportRecord[]}>(
    dismissImports,
    {
      onMutate: async (request) => {
        await queryClient.cancelQueries(importsQueryKey)
        const previous = queryClient.getQueryData<ImportRecord[]>(importsQueryKey)
        const hidden = (record: ImportRecord) =>
          'allFinished' in request ? isFinishedImport(record) : request.importIds.includes(record.importId)
        queryClient.setQueryData<ImportRecord[]>(importsQueryKey, (old) => old?.filter((r) => !hidden(r)))
        return {previous}
      },
      onError: (error, _request, context) => {
        queryClient.setQueryData(importsQueryKey, context?.previous)
        handleAuthError(error)
      },
      onSettled: () => queryClient.invalidateQueries(importsQueryKey),
    }
  )
}
